import { useState, useEffect, useCallback } from 'react';
import {
  getTodos,
  addTodo as _add,
  updateTodo as _update,
  completeTodo as _complete,
  reopenTodo as _reopen,
  deleteTodo as _delete,
  pruneOldCompleted,
} from '@/services/todoService';
import { useAppContext } from '@/context/AppContext';

export function useTodos() {
  const [todos, setTodos]   = useState([]);
  const [loading, setLoading] = useState(true);
  const { showToast }       = useAppContext();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      await pruneOldCompleted();
      const data = await getTodos();
      setTodos(data);
    } catch (err) {
      console.error('useTodos error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addTodo = useCallback(async (todo) => {
    await _add(todo);
    await load();
    showToast('Task added ✓', 'success');
  }, [load, showToast]);

  const updateTodo = useCallback(async (id, fields) => {
    await _update(id, fields);
    await load();
    showToast('Task updated ✓', 'success');
  }, [load, showToast]);

  const completeTodo = useCallback(async (id, comment) => {
    await _complete(id, comment);
    setTodos(prev => prev.map(t => t.id === id
      ? { ...t, status: 'done', completedAt: new Date().toISOString(), completionComment: comment }
      : t
    ));
    showToast('Task completed ✓', 'success');
  }, [showToast]);

  const reopenTodo = useCallback(async (id) => {
    await _reopen(id);
    setTodos(prev => prev.map(t => t.id === id
      ? { ...t, status: 'pending', completedAt: null }
      : t
    ));
    showToast('Task reopened', 'info');
  }, [showToast]);

  const deleteTodo = useCallback(async (id) => {
    await _delete(id);
    setTodos(prev => prev.filter(t => t.id !== id));
    showToast('Task deleted', 'info');
  }, [showToast]);

  return { todos, loading, addTodo, updateTodo, completeTodo, reopenTodo, deleteTodo, refetch: load };
}
